import React from "react"
import { useTranslation } from "react-i18next"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import useAppContext from "~/hooks/useAppContext"
import useSetIsSidebarOpen from "~/hooks/useSetIsSidebarOpen"
import { PanelType } from "~/constants/app-options"
import { CONTENT_KINDS, ContentKind, KIND_ORDER, designTypeOfKind } from "~/constants/content-kinds"
import { ANIMATED_TEMPLATES } from "~/constants/animatedTemplates"
import { formatSizeLabel, formatsForKind } from "~/constants/formats"
import type { DesignFormat } from "~/constants/formats"
import LanguageSwitcher from "~/components/LanguageSwitcher"
import Icons from "~/components/Icons"

/* -- small inline icons (Canva-like rail) ---------------------------------- */

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
}

function IconPlus({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
function IconSparkles({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M12 3l1.8 4.9L19 9.7l-5.2 1.8L12 16.4l-1.8-4.9L5 9.7l5.2-1.8L12 3z" />
      <path d="M18.5 14.5l.9 2.4 2.6.9-2.6.9-.9 2.4-.9-2.4-2.6-.9 2.6-.9.9-2.4z" />
    </svg>
  )
}
function IconHome({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M4 10.5L12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5z" />
    </svg>
  )
}
function IconFolder({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
    </svg>
  )
}
function IconGrid({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </svg>
  )
}
function IconBrand({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <path d="M4 10v4a1 1 0 0 0 1 1h3l6 4V5L8 9H5a1 1 0 0 0-1 1z" />
      <path d="M18 9a4 4 0 0 1 0 6" />
    </svg>
  )
}
function IconMore({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <circle cx="5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  )
}
function IconSearch({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" {...stroke}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </svg>
  )
}

/* -- data ------------------------------------------------------------------ */

const CARD_TYPES: Array<{ id: string; label: string; kind: ContentKind; ratio: number; color: string }> = [
  { id: "presentation", label: "Apresentação", kind: "PRESENTATION", ratio: 4 / 3, color: "linear-gradient(150deg,#dcefff,#bfe0f6)" },
  { id: "document", label: "Documento", kind: "DIGITAL", ratio: 3 / 4, color: "linear-gradient(150deg,#d7ecf9,#b9dff2)" },
  { id: "flyer", label: "Panfleto", kind: "PRINT", ratio: 3 / 4, color: "linear-gradient(150deg,#e0e7fb,#c3cff6)" },
  { id: "poster", label: "Cartaz", kind: "PRINT", ratio: 3 / 4, color: "linear-gradient(150deg,#dae4fb,#bccbf5)" },
  { id: "video", label: "Vídeo horizontal", kind: "VIDEO", ratio: 16 / 9, color: "linear-gradient(150deg,#d3e8fd,#b3d2f8)" },
  { id: "doc", label: "Doc", kind: "DIGITAL", ratio: 16 / 9, color: "linear-gradient(150deg,#dbeefb,#bfdff6)" },
  { id: "story", label: "Story", kind: "DIGITAL", ratio: 9 / 16, color: "linear-gradient(150deg,#e3e8fc,#c7d0f7)" },
  { id: "instagram", label: "Post para Instagram", kind: "DIGITAL", ratio: 1, color: "linear-gradient(150deg,#e6f2fb,#c9e2f5)" },
  { id: "resume", label: "Currículo", kind: "DIGITAL", ratio: 3 / 4, color: "linear-gradient(150deg,#e8f1fc,#cddff6)" },
  { id: "logo", label: "Logo", kind: "DIGITAL", ratio: 1, color: "linear-gradient(150deg,#d9eafc,#bbd8f6)" },
  { id: "thumbnail", label: "Miniatura do YouTube", kind: "DIGITAL", ratio: 16 / 9, color: "linear-gradient(150deg,#dfe7fc,#c3d2f7)" },
  { id: "whiteboard", label: "Quadro branco", kind: "PRESENTATION", ratio: 16 / 9, color: "linear-gradient(150deg,#e2f2f9,#c5e5f3)" },
]

const CATEGORY_FILTERS: Array<{ id: string; label: string; cats: string[] }> = [
  { id: "business", label: "Empresarial", cats: ["Business", "Real Estate"] },
  { id: "social", label: "Redes sociais", cats: ["Marketing", "Sales & Products"] },
  { id: "video", label: "Vídeo", cats: [] },
  { id: "education", label: "Educação", cats: ["Education"] },
]

interface TemplateItem {
  id: string
  name: string
  catName?: string
  thumb?: string
  width?: number
  height?: number
  kind?: ContentKind
}

/** Até a curadoria existir, todo template importado é estático (não tem layer de vídeo). */
const kindOfTemplate = (item: TemplateItem): ContentKind => item.kind || "DIGITAL"

/** Os templates animados nativos são o catálogo inicial de vídeo. */
const ANIMATED_ITEMS: TemplateItem[] = ANIMATED_TEMPLATES.map((template: any) => ({
  id: template.id,
  name: template.name,
  catName: "Animated",
  thumb: template.preview,
  width: template.frame?.width,
  height: template.frame?.height,
  kind: "VIDEO",
}))

function pickByRatio(items: TemplateItem[], ratio: number): TemplateItem | undefined {
  let best: TemplateItem | undefined
  let bestScore = Number.POSITIVE_INFINITY
  for (const item of items) {
    if (!item.thumb) continue
    const r = (item.width || 1) / (item.height || 1)
    const score = Math.abs(Math.log(r / ratio))
    if (score < bestScore) {
      bestScore = score
      best = item
    }
  }
  return best
}

/* -- component ------------------------------------------------------------- */

export default function SelectEditor() {
  const { setEditorType, setContentKind, setActiveFormat, setScenes, setCurrentScene } = useDesignEditorContext()
  const { setActivePanel } = useAppContext()
  const setIsSidebarOpen = useSetIsSidebarOpen()
  const { t } = useTranslation("editor")
  const [templates, setTemplates] = React.useState<TemplateItem[]>([])
  const [query, setQuery] = React.useState("")
  const [category, setCategory] = React.useState("all")
  const [kindFilter, setKindFilter] = React.useState<"ALL" | ContentKind>("ALL")
  const [formatPicker, setFormatPicker] = React.useState<ContentKind | null>(null)
  const [pendingTemplates, setPendingTemplates] = React.useState(false)
  const [tab, setTab] = React.useState("templates")
  const exploreRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    let cancelled = false
    fetch("/user-templates/index.json", { cache: "force-cache" })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!cancelled) setTemplates([...ANIMATED_ITEMS, ...(Array.isArray(data) ? data : [])])
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = React.useMemo(() => {
    const catFilter = CATEGORY_FILTERS.find((c) => c.id === category)
    const q = query.trim().toLowerCase()
    return templates.filter((item) => {
      if (q && !(item.name || "").toLowerCase().includes(q)) return false
      if (kindFilter !== "ALL" && kindOfTemplate(item) !== kindFilter) return false
      if (!catFilter || catFilter.id === "video") return true
      return catFilter.cats.includes(item.catName || "")
    })
  }, [templates, query, category, kindFilter])

  const discover = React.useMemo(() => {
    const named = filtered.filter((item) => {
      const name = (item.name || "").trim()
      return name.length >= 4 && /[a-zA-ZÀ-ÿ]/.test(name)
    })
    const source = named.length ? named : filtered
    const preferred = source.filter((item) => (item.width || 1) >= (item.height || 1))
    return (preferred.length ? preferred : source).slice(0, 12)
  }, [filtered])

  const counts = React.useMemo(() => {
    const result: Record<string, number> = { ALL: templates.length }
    for (const kind of KIND_ORDER) result[kind] = 0
    for (const item of templates) result[kindOfTemplate(item)] += 1
    return result
  }, [templates])

  const openEditor = (kind: ContentKind, withTemplates = false) => {
    if (formatsForKind(kind).length) {
      setPendingTemplates(withTemplates)
      setFormatPicker(kind)
      return
    }
    setActiveFormat(null)
    setContentKind(kind)
    if (withTemplates) {
      setActivePanel(PanelType.TEMPLATES)
      setIsSidebarOpen(true)
    }
    setEditorType(designTypeOfKind(kind))
  }

  const chooseFormat = (format: DesignFormat) => {
    setActiveFormat(format)
    setContentKind(format.kind)
    // Um formato novo começa um design novo: descarta as páginas/cenas anteriores.
    setScenes([])
    setCurrentScene(null)
    if (pendingTemplates) {
      setActivePanel(PanelType.TEMPLATES)
      setIsSidebarOpen(true)
    }
    setFormatPicker(null)
    setEditorType(designTypeOfKind(format.kind))
  }

  const scrollToExplore = () => {
    setTab("templates")
    exploreRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div className="ar-home">
      <aside className="ar-home-rail">
        <div className="ar-rail-brand" title="AgentResolve">
          <Icons.Logo size={30} />
        </div>

        <button className="ar-rail-cta" onClick={() => openEditor("DIGITAL")} title={t("home.create", "Criar")}>
          <IconPlus size={20} />
          <span>{t("home.create", "Criar")}</span>
        </button>

        <button className="ar-rail-item" onClick={() => openEditor("DIGITAL", true)} title={t("home.ai", "Agent IA")}>
          <IconSparkles size={21} />
          <span>{t("home.ai", "Agent IA")}</span>
        </button>
        <button className="ar-rail-item active" title={t("home.inicio", "Início")}>
          <IconHome size={21} />
          <span>{t("home.inicio", "Início")}</span>
        </button>
        <button className="ar-rail-item" disabled title={t("home.projects", "Projetos")}>
          <IconFolder size={21} />
          <span>{t("home.projects", "Projetos")}</span>
        </button>
        <button className="ar-rail-item" onClick={scrollToExplore} title={t("home.templates", "Modelos")}>
          <IconGrid size={21} />
          <span>{t("home.templates", "Modelos")}</span>
        </button>
        <button className="ar-rail-item" disabled title={t("home.brand", "Marca")}>
          <IconBrand size={21} />
          <span>{t("home.brand", "Marca")}</span>
        </button>
        <button className="ar-rail-item" disabled title={t("home.more", "Mais")}>
          <IconMore size={21} />
          <span>{t("home.more", "Mais")}</span>
        </button>

        <div className="ar-rail-spacer" />
        <div className="ar-rail-footer">
          <LanguageSwitcher compact />
          <div className="ar-avatar">A</div>
        </div>
      </aside>

      <main className="ar-home-main">
        <section className="ar-hero">
          <div className="ar-hero-top">
            <button className="ar-pro-badge" onClick={() => window.open("https://veditor.agenteresolve.com.br/billing/plans", "_blank")}>
              <span aria-hidden>✦</span>
              {t("home.pro", "Experimente grátis por 30 dias")}
            </button>
          </div>

          <h1>{t("home.title", "Bora fazer bonito?")}</h1>
          <p className="ar-hero-sub">
            {t("home.subtitle", "Crie vídeos, apresentações e designs com IA — em minutos.")}
          </p>

          <div className="ar-hero-tabs">
            <button className={`ar-hero-tab ${tab === "home" ? "active" : ""}`} onClick={() => setTab("home")}>
              <IconHome size={16} />
              {t("home.tabHome", "Início")}
            </button>
            <button className={`ar-hero-tab ${tab === "templates" ? "active" : ""}`} onClick={() => setTab("templates")}>
              <IconGrid size={16} />
              {t("home.tabTemplates", "Modelos")}
            </button>
          </div>

          <div className="ar-search">
            <IconSearch size={20} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("home.search", "Busque entre milhões de modelos")}
              aria-label={t("home.search", "Busque entre milhões de modelos")}
            />
          </div>

          <div className="ar-pills">
            <button
              className={`ar-pill ${category === "all" ? "active" : ""}`}
              onClick={() => setCategory("all")}
            >
              {t("home.all", "Todos")}
            </button>
            {CATEGORY_FILTERS.map((filter) => (
              <button
                key={filter.id}
                className={`ar-pill ${category === filter.id ? "active" : ""}`}
                onClick={() => setCategory(filter.id)}
              >
                {t(`home.cats.${filter.id}`, filter.label)}
              </button>
            ))}
          </div>
        </section>

        <div ref={exploreRef}>
          <section className="ar-section">
            <div className="ar-section-head">
              <h2>{t("home.explore", "Explorar modelos")}</h2>
            </div>
            <div className="ar-row">
              {CARD_TYPES.map((card) => {
                const sameKind = templates.filter((item) => kindOfTemplate(item) === card.kind)
                const thumb = pickByRatio(sameKind, card.ratio)
                return (
                  <button
                    key={card.id}
                    className="ar-card"
                    style={{ background: card.color }}
                    onClick={() => openEditor(card.kind)}
                    title={t(`home.cards.${card.id}`, card.label)}
                  >
                    <span className="ar-card-badge">{t(CONTENT_KINDS[card.kind].labelKey)}</span>
                    {thumb?.thumb && <img className="ar-card-img" src={thumb.thumb} alt="" loading="lazy" />}
                    <h3>{t(`home.cards.${card.id}`, card.label)}</h3>
                  </button>
                )
              })}
            </div>
          </section>
        </div>

        <section className="ar-section">
          <div className="ar-section-head">
            <h2>{t("home.discover", "Descubra o AgentResolve")}</h2>
            <button className="ar-link" onClick={() => openEditor(kindFilter === "ALL" ? "DIGITAL" : kindFilter, true)}>
              {t("home.seeAll", "Ver tudo")}
            </button>
          </div>

          <div className="ar-pills ar-kind-filters">
            <button
              className={`ar-pill ${kindFilter === "ALL" ? "active" : ""}`}
              onClick={() => setKindFilter("ALL")}
            >
              {t("kinds.all", "Todos os tipos")}
              <span className="ar-pill-count">{counts.ALL}</span>
            </button>
            {KIND_ORDER.map((kind) => (
              <button
                key={kind}
                className={`ar-pill ${kindFilter === kind ? "active" : ""}`}
                onClick={() => setKindFilter(kind)}
                title={t(CONTENT_KINDS[kind].hintKey)}
              >
                {t(CONTENT_KINDS[kind].labelKey)}
                <span className="ar-pill-count">{counts[kind] || 0}</span>
              </button>
            ))}
          </div>

          {discover.length === 0 ? (
            <div className="ar-home-empty">{t("home.empty", "Nenhum modelo encontrado para esta busca.")}</div>
          ) : (
            <div className="ar-row">
              {discover.map((item) => (
                <button
                  key={item.id}
                  className="ar-discover"
                  onClick={() => openEditor(kindOfTemplate(item))}
                  title={item.name}
                >
                  {item.thumb && <img src={item.thumb} alt={item.name} loading="lazy" />}
                  <span className="ar-discover-label">
                    <span className="ar-discover-name">{item.name}</span>
                    <span>{item.catName || ""}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </section>
      </main>

      {formatPicker && (
        <div className="ar-modal-backdrop" onClick={() => setFormatPicker(null)}>
          <div className="ar-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="ar-modal-head">
              <h2>{t("formats.pickTitle", "Escolha o formato")}</h2>
              <button
                className="ar-modal-close"
                onClick={() => setFormatPicker(null)}
                aria-label={t("formats.close", "Fechar")}
              >
                ×
              </button>
            </div>
            <p className="ar-modal-sub">{t(CONTENT_KINDS[formatPicker].hintKey)}</p>
            <div className="ar-format-grid">
              {formatsForKind(formatPicker).map((format) => (
                <button key={format.id} className="ar-format-card" onClick={() => chooseFormat(format)}>
                  <span className="ar-format-name">{t(`formats.${format.id}`, format.name)}</span>
                  <span className="ar-format-size">{formatSizeLabel(format)}</span>
                  <span className="ar-format-meta">
                    {format.kind === "PRINT"
                      ? `${t("formats.pages", { count: format.pages })} · ${t("formats.bleedShort", "sangria")} ${format.bleed} mm · ${t(
                          "formats.safeShort",
                          "área segura"
                        )} ${format.safe} mm`
                      : t("formats.singlePage", "Página única")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
